import { useParams, Link, Navigate, useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { SEOHead } from '../components/SEOHead';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { NewsletterSignup } from '../components/NewsletterSignup';
import { AuthorByline, AuthorBottomCard, authorRef } from '../components/AuthorComponents';
import { BLOG_POSTS, BLOG_CATEGORIES } from '../data/blogPosts';
import { AR_BLOG_SLUGS, isArPath } from '../i18nRoutes';
import { translations } from '../translations';
import { useRestaurants } from '../lib/useRestaurants';
import { restaurantPicksHtml, RESTAURANT_PICKS_MARKER } from '../lib/restaurantPicksHtml';

export default function BlogPostPage({ language, setLanguage }) {
  const { slug } = useParams();
  const location = useLocation();
  const t = translations[language];
  const post = BLOG_POSTS.find(p => p.slug === slug);
  const { restaurants } = useRestaurants();
  const onArRoute = isArPath(location.pathname);

  if (!post) return <Navigate to="/blog" replace />;
  if (onArRoute && !AR_BLOG_SLUGS.includes(slug)) return <Navigate to={`/blog/${slug}`} replace />;

  const isAr = onArRoute;
  const title = isAr ? (post.titleAr || post.title) : post.title;
  const rawContent = isAr ? (post.contentAr || post.content) : post.content;
  const content = rawContent.includes(RESTAURANT_PICKS_MARKER) ? rawContent.replace(RESTAURANT_PICKS_MARKER, restaurantPicksHtml(restaurants, isAr)) : rawContent;
  const cat = BLOG_CATEGORIES.find(c => c.key === post.category);
  const catLabel = isAr ? (cat?.labelAr || post.category) : (cat?.label || post.category);
  const pageUrl = `https://viaturkey.net${isAr ? '/ar' : ''}/blog/${post.slug}`;

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": title,
        "author": authorRef,
        "publisher": { "@type": "Organization", "name": "Viaturkey", "url": "https://viaturkey.net" },
        "datePublished": post.date,
        "image": post.thumbnail,
        "inLanguage": isAr ? "ar" : "en",
        "url": pageUrl
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://viaturkey.net/" },
          { "@type": "ListItem", "position": 2, "name": "Blog", "item": `https://viaturkey.net${isAr ? '/ar' : ''}/blog` },
          { "@type": "ListItem", "position": 3, "name": title, "item": pageUrl }
        ]
      }
    ]
  };

  const pubDate = new Date(post.date).toLocaleDateString(isAr ? 'ar-EG' : 'en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="blog-post-page" dir={isAr ? 'rtl' : 'ltr'} data-testid="blog-post-page">
      <SEOHead
        title={isAr ? (post.metaTitleAr || post.titleAr || post.metaTitle) : post.metaTitle}
        description={isAr ? (post.metaDescriptionAr || post.excerptAr || post.metaDescription) : post.metaDescription}
        canonical={`https://viaturkey.net/blog/${post.slug}`}
        type="article"
        schema={schema}
      />
      <Header language={language} t={t} />

      <article className="blog-post" data-testid="blog-post-content">
        <div className="blog-post-header">
          <Link to={isAr ? '/ar/blog' : '/blog'} className="blog-back-link" data-testid="blog-back-link"><i className={`fas fa-arrow-${isAr ? 'right' : 'left'}`}></i> {isAr ? 'العودة إلى المدونة' : 'Back to Blog'}</Link>
          <Link to={`/blog/category/${post.category}`} className="blog-post-cat" data-testid="blog-post-cat">{catLabel}</Link>
          <h1 lang={isAr ? 'ar' : 'en'}>{title}</h1>
          <div className="blog-post-meta">
            <time>{pubDate}</time>
            <span>{isAr ? post.readTime.replace(' min', ' دقائق') : `${post.readTime} read`}</span>
          </div>
          <img src={post.thumbnail.replace('w=600', 'w=1200')} alt={title} loading="lazy" className="blog-post-hero-img" />
          <AuthorByline publishDate={pubDate} updateDate="June 2026" />
        </div>

        <div className="blog-post-body" lang={isAr ? 'ar' : 'en'} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }} />

        <AuthorBottomCard />

        <div className="blog-cta" data-testid="blog-cta">
          <h3>{isAr ? 'مستعد لتجربة إسطنبول؟' : 'Ready to Experience Istanbul?'}</h3>
          <p>{isAr ? 'راسلنا مباشرة على واتساب للحصول على استشارة مخصصة.' : 'Message us directly on WhatsApp for a personalized consultation.'}</p>
          <a href="https://wa.me/905075337545?text=Hi%20Viaturkey!%20I%20read%20your%20blog%20and%20I'm%20interested." target="_blank" rel="noopener noreferrer" className="blog-cta-btn" data-testid="blog-whatsapp-btn">
            <i className="fab fa-whatsapp"></i> {isAr ? 'خطط رحلتي' : 'Plan My Trip'}
          </a>
        </div>

        <NewsletterSignup language={language} />
      </article>

      <Footer t={t} />
    </div>
  );
}
